/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { isError } from "@epic-mw/error-types";
import useSWRImmutable from "swr/immutable";

import { useApiConfig } from "../api-config";
import { getApiPath } from "../utils";

export type SeatFunnelFlag = "ue-seat-funnel-v2" | "tm-seat-funnel-v2" | "rs-seat-funnel-v2";

// Fail closed on missing flags or failed fetches: existing purchase flows stay intact.
const defaultLaunchDarklyFlagValues = {
	"editor-user-agent-redirect": false,
	"ue-seat-funnel-v2": false,
	"tm-seat-funnel-v2": false,
	"rs-seat-funnel-v2": false,
};

export const getFlag = async <T extends keyof typeof defaultLaunchDarklyFlagValues>(
	flag: T,
	serverUrl?: string,
): Promise<(typeof defaultLaunchDarklyFlagValues)[T]> => {
	let flags: unknown = (window as Window & { _epicFlags?: unknown })._epicFlags;

	if (!flags) {
		try {
			const response = await fetch(`${getApiPath(serverUrl)}/launch-darkly-flags`);
			if (!response.ok) throw new Error(`Flag request failed: ${response.status}`);
			flags = await response.json();
		} catch (err) {
			console.error("flags() failed to load", isError(err) && err.message);
		}
	}

	if (flags && typeof flags === "object") {
		const value = (flags as Record<string, unknown>)[flag];
		if (typeof value === "boolean") return value;
	}
	return defaultLaunchDarklyFlagValues[flag];
};

export const useFlag = <T extends keyof typeof defaultLaunchDarklyFlagValues>(
	flag?: T,
): {
	value?: (typeof defaultLaunchDarklyFlagValues)[T];
	isLoading: boolean;
} => {
	const { serverUrl } = useApiConfig();
	/*
	  No error channel and no retry, because neither could ever fire. getFlag catches every
	  failure and resolves the fail-closed default, so SWR always records a success: an `error`
	  field would be permanently undefined and an `errorRetryCount` would never trigger. A caller
	  therefore cannot distinguish "the flag is false" from "the flag service is unreachable", and
	  both mean the same thing here — keep the existing flow.
	*/
	const { data } = useSWRImmutable(flag ? [`flags-${flag}`, serverUrl] : null, () => flag && getFlag(flag, serverUrl));

	return {
		value: data,
		isLoading: !!flag && data === undefined,
	};
};
